import {
  cartSchema,
  hotelCityListSchema,
  hotelSearchResultSchema,
  packageSummarySchema,
} from '~/schemas'
import { fetchApi, isFetchApiError, getFetchApiErrorStatus } from './helpers'
import { normalizeCurrencyCode } from '~/utils/currency'

type HotelGuestRoomInput = {
  room_type_id?: string | number | null
  roomTypeId?: string | number | null
  adults: number
  children?: number
  children_ages?: number[]
  childrenAges?: number[]
}

type HotelSearchInput = {
  provider?: string | null
  date: string
  nights: number
  rooms?: Record<string, number>
  guest_rooms?: HotelGuestRoomInput[]
  guestRooms?: HotelGuestRoomInput[]
  city?: string | null
  city_id?: string | number | null
  cityId?: string | number | null
  currency?: string | null
  language?: string | null
}

export type HotelGuestRoom = {
  roomTypeId: string | null
  adults: number
  children: number
  childrenAges: number[]
}

export type HotelPricing = {
  net?: number | null
  currency?: string | null
  displayNet: number
  displayTotal: number
  displayCurrency?: string | null
  sourceNet?: number | null
  sourceCurrency?: string | null
  margin?: number | null
}

export type HotelOffer = {
  provider?: string | null
  hotelId: string
  name: string
  address?: string | null
  city?: string | null
  latitude?: number | null
  longitude?: number | null
  description?: string | null
  roomInfo?: string | null
  locationInfo?: string | null
  images: string[]
  rooms: any[]
  stars?: number | null
  breakfast: boolean
  wifi: boolean
  checkIn?: string | null
  checkOut?: string | null
  supplierCancellationPolicy?: any
  pricing: HotelPricing
  raw?: any
}

export type HotelSearchCriteria = {
  provider?: string | null
  date: string
  nights: number
  rooms: Record<string, number>
  guestRooms: HotelGuestRoom[]
  occupancy: any[]
  city?: string | null
  cityId?: string | number | null
  currency?: string | null
  language?: string | null
}

export type HotelSearchData = {
  provider?: string | null
  criteria: HotelSearchCriteria
  items: HotelOffer[]
  raw?: any
}

const toNumberOrNull = (value: unknown) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && value.trim() === '') return null

  const numberValue = Number(value)

  return Number.isFinite(numberValue) ? numberValue : null
}

const getValue = (source: unknown, keys: string[]) => {
  if (!source || typeof source !== 'object') return null

  const record = source as Record<string, any>

  for (const key of keys) {
    const value = key.includes('.')
      ? key.split('.').reduce((item, part) => item?.[part], record)
      : record[key]

    if (value !== null && value !== undefined && value !== '') return value
  }

  return null
}

const toHotelStarRating = (value: unknown) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const rating = value as Record<string, unknown>

    return toHotelStarRating(rating.value ?? rating.rating ?? rating.stars)
  }

  const normalizedValue =
    typeof value === 'string'
      ? value.trim().match(/\d+(?:\.\d+)?/)?.[0] ?? value
      : value
  const rating = toNumberOrNull(normalizedValue)

  if (rating === null || rating < 0 || rating > 5) return null

  return rating
}

const getHotelStars = (offer: any) => {
  const starKeys = [
    'stars',
    'star_rating',
    'starRating',
    'star_raiting',
    'starRaiting',
    'hotel_rating',
    'hotelRating',
    'rating',
    'hotel.star_rating',
    'hotel.starRating',
    'hotel.rating',
    'data.star_rating',
    'data.starRating',
    'data.rating',
  ]

  return toHotelStarRating(
    getValue(offer, starKeys) ?? getValue(offer?.raw, starKeys),
  )
}

const normalizeImages = (images: unknown) => {
  if (!Array.isArray(images)) return []

  return images
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object') {
        const image = item as Record<string, any>

        return image.large || image.small || image.url || image.data?.url
      }

      return null
    })
    .filter((item): item is string => typeof item === 'string' && item.length > 0)
}

const normalizeHotelRooms = (
  rooms: unknown,
  fallbackRooms: unknown = [],
): Record<string, number> => {
  if (rooms && typeof rooms === 'object' && !Array.isArray(rooms)) {
    return Object.fromEntries(
      Object.entries(rooms as Record<string, unknown>)
        .map(([roomId, quantity]) => [roomId, Number(quantity)])
        .filter(([roomId, quantity]) => roomId !== '' && Number.isFinite(quantity)),
    )
  }

  if (!Array.isArray(rooms)) return {}

  return Object.fromEntries(
    rooms
      .map((room, index) => {
        if (room && typeof room === 'object') {
          const roomData = room as Record<string, any>
          const roomId = roomData.room_id ?? roomData.roomId ?? roomData.id

          return roomId !== undefined && roomId !== null
            ? [String(roomId), Number(roomData.quantity ?? 1)]
            : null
        }

        const fallbackRoom = Array.isArray(fallbackRooms)
          ? (fallbackRooms[index] as Record<string, any> | undefined)
          : undefined
        const roomId =
          fallbackRoom?.room_id ??
          fallbackRoom?.roomId ??
          fallbackRoom?.id ??
          index

        return [String(roomId), Number(room)]
      })
      .filter(
        (entry): entry is [string, number] =>
          Array.isArray(entry) &&
          entry[0] !== '' &&
          Number.isFinite(entry[1]) &&
          entry[1] > 0,
      ),
  )
}

const mapHotelGuestRoom = (room: any): HotelGuestRoom => ({
  roomTypeId:
    room?.room_type_id !== undefined && room?.room_type_id !== null
      ? String(room.room_type_id)
      : room?.roomTypeId !== undefined && room?.roomTypeId !== null
        ? String(room.roomTypeId)
        : null,
  adults: Number(room?.adults ?? 1),
  children: Number(room?.children ?? 0),
  childrenAges: (room?.children_ages || room?.childrenAges || []).map((age: any) =>
    Number(age),
  ),
})

const mapHotelPricing = (pricing: any = {}): HotelPricing => ({
  net: pricing.net ?? null,
  currency: pricing.currency ?? null,
  displayNet: pricing.display_net ?? pricing.displayNet ?? pricing.net ?? 0,
  displayTotal:
    pricing.display_total ??
    pricing.displayTotal ??
    pricing.total ??
    pricing.net ??
    0,
  displayCurrency: pricing.display_currency ?? pricing.displayCurrency ?? null,
  sourceNet: pricing.source_net ?? pricing.sourceNet ?? null,
  sourceCurrency: pricing.source_currency ?? pricing.sourceCurrency ?? null,
  margin: pricing.margin ?? null,
})

const mapHotelOffer = (offer: any): HotelOffer => ({
  ...offer,
  provider: offer?.provider ?? null,
  hotelId: String(offer?.hotel_id ?? offer?.hotelId ?? offer?.id ?? ''),
  name: offer?.name ?? offer?.hotel_name ?? offer?.HotelName ?? '',
  address:
    typeof offer?.address === 'string'
      ? offer.address
      : [
          offer?.address?.street_name,
          offer?.address?.city,
          offer?.address?.state,
          offer?.address?.post_code,
          offer?.address?.country,
        ]
          .filter(Boolean)
          .join(', ') || null,
  city: offer?.city ?? offer?.address?.city ?? null,
  latitude: toNumberOrNull(offer?.latitude ?? offer?.lat ?? offer?.address?.lat),
  longitude: toNumberOrNull(offer?.longitude ?? offer?.lng ?? offer?.address?.lng),
  description: offer?.description ?? offer?.information ?? offer?.hotel_info ?? null,
  roomInfo: offer?.room_info ?? offer?.roomInfo ?? null,
  locationInfo: offer?.location_info ?? offer?.locationInfo ?? null,
  images: normalizeImages(
    offer?.images ||
      offer?.photos ||
      [
        offer?.image_large,
        offer?.image_small,
        ...(offer?.image_gallery || []),
      ].filter(Boolean),
  ),
  rooms: Array.isArray(offer?.rooms)
    ? offer.rooms
    : Array.isArray(offer?.price_breakdown)
      ? offer.price_breakdown
      : [],
  stars: getHotelStars(offer),
  breakfast: Boolean(offer?.breakfast),
  wifi: Boolean(offer?.wifi),
  checkIn: offer?.check_in ?? offer?.checkIn ?? null,
  checkOut: offer?.check_out ?? offer?.checkOut ?? null,
  supplierCancellationPolicy:
    offer?.supplier_cancellation_policy ??
    offer?.supplierCancellationPolicy ??
    offer?.cancellations ??
    null,
  pricing: mapHotelPricing(offer?.pricing),
  raw: offer?.raw ?? offer,
})

const mapHotelSearchCriteria = (
  criteria: any = {},
  fallbackRooms: unknown = [],
): HotelSearchCriteria => ({
  ...criteria,
  provider: criteria.provider ?? null,
  date: criteria.date,
  nights: criteria.nights,
  rooms: normalizeHotelRooms(criteria.rooms, fallbackRooms),
  guestRooms: (criteria.guest_rooms || criteria.guestRooms || []).map(mapHotelGuestRoom),
  occupancy: criteria.occupancy || [],
  city: criteria.city ?? null,
  cityId: criteria.city_id ?? criteria.cityId ?? null,
  currency: criteria.currency ?? null,
  language: criteria.language ?? null,
})

const mapHotelSearchResult = (response: any): HotelSearchData =>
  hotelSearchResultSchema.parse({
    provider: response.data?.provider,
    criteria: mapHotelSearchCriteria(response.data?.criteria || {}),
    items: (response.data?.items || []).map(mapHotelOffer),
    raw: response.data?.raw,
  }) as HotelSearchData

const mapHotelAddOn = (addOn: any) => ({
  ...addOn,
  id: addOn.id,
  type: addOn.type,
  packageId: addOn.package_id ?? null,
  provider: addOn.provider ?? null,
  search: mapHotelSearchCriteria(addOn.search || {}, addOn.offer?.rooms),
  offer: mapHotelOffer(addOn.offer || {}),
  rooms: normalizeHotelRooms(
    addOn.rooms || addOn.search?.rooms,
    addOn.offer?.rooms,
  ),
  guestRooms: (addOn.guest_rooms || addOn.guestRooms || []).map(mapHotelGuestRoom),
  pricing: mapHotelPricing(addOn.pricing),
  cancellationPolicy: addOn.cancellation_policy ?? addOn.cancellationPolicy ?? null,
  selectedAt: addOn.selected_at ?? addOn.selectedAt ?? null,
})

const parseVenueCoordinates = (
  value: unknown,
): { latitude: number | null; longitude: number | null } => {
  if (typeof value !== 'string') {
    return { latitude: null, longitude: null }
  }

  const parts = value.split(',').map((part) => Number(part.trim()))

  if (parts.length !== 2) {
    return { latitude: null, longitude: null }
  }

  const [latitude, longitude] = parts

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return { latitude: null, longitude: null }
  }

  return { latitude, longitude }
}

const cartItemMapper = (item: any) => {
  const giftCard = item.data?.gift_card || item.gift_card

  return {
    id: item.data?.id,
    type: item.product?.data?.type,
    createdAt: item.data?.created_at,
    expiresAt: item.data?.expires_at,
    paymentExpiresAt: item.data?.payment_expires_at,
    dateTime: item.data?.datetime,
    image: item.product.extra.default_image?.data?.url ||
      item.product.media?.[0]?.data?.url,
    title: item.product.extra.translation?.data?.name,
    startDate: item.product.data?.start_date,
    startTime: item.product.data?.start_time,
    productId: item.product.data?.id,
    url: item.product.extra.url?.data?.url,
    productTitle: item.product?.extra.translation?.data?.name,
    // The ticket category value already has the club setting folded in, so it
    // wins whenever the item has one.
    packageId: item.data?.package_id ?? null,
    mandatoryHotel: item.ticket_category
      ? !!item.ticket_category.data?.mandatory_hotel
      : !!item.product?.options?.data?.mandatory_hotel,
    venue: {
      name: item.product.venue?.extra?.translation?.data?.name,
      address: item.product.venue?.extra?.translation?.data?.address,
      ...parseVenueCoordinates(item.product.venue?.data?.coordinates),
    },
    ticketCategory: item.ticket_category
      ? {
        id: item.ticket_category.data?.id,
        price: item.ticket_category.data?.price,
        minQuantity: item.ticket_category.data?.quantity_min,
        maxQuantity: item.ticket_category.data?.quantity_max,
        title: item.ticket_category.translation?.data?.name,
        description: item.ticket_category.translation?.data?.description,
        mandatoryHotel: !!item.ticket_category.data?.mandatory_hotel,
      }
      : undefined,
    performance: item.performance
      ? {
        id: item.performance.data?.id,
        externalId: item.performance.data?.external_id,
        dateTime: item.performance.data?.datetime,
      }
      : undefined,
    giftCard: giftCard
      ? {
        value: giftCard.value ?? null,
        recipientName: giftCard.recipient_name ?? giftCard.recipientName ?? null,
        recipientEmail: giftCard.recipient_email ?? giftCard.recipientEmail ?? null,
        message: giftCard.message ?? null,
      }
      : undefined,
    variations: item.data?.variations?.map((item: Record<string, any>) => ({
      id: item.id,
      price: item.price,
      discount: item.discount,
      wasPrice: item.was_price,
      discountPercent: item.discount_percent,
      quantity: item.quantity,
      name: item.name,
      description: item.description,
    })),
  }
}

const mapCartData = (response: any) =>
  cartSchema.parse({
    currency: response.data?.currency,
    id: response.data?.id,
    discount: response.data?.discount,
    subtotal: response.data?.subtotal,
    total: response.data?.total,
    giftCardTotal: response.data?.gift_card_total ?? 0,
    totalPayable: response.data?.total_payable ?? response.data?.total,
    appliedGiftCards: (response.data?.applied_gift_cards || []).map((item: any) => ({
      code: item.code,
      status: item.status,
      appliedAmount: item.applied_amount,
      remainingBalance: item.remaining_balance ?? null,
    })),
    invalidGiftCards: (response.data?.invalid_gift_cards || []).map((item: any) => ({
      code: item.code,
      status: item.status,
      errors: Array.isArray(item.errors)
        ? item.errors.flatMap((error: any) => Array.isArray(error) ? error : [error])
        : Object.values(item.errors || {}).flatMap((error: any) => Array.isArray(error) ? error : [error]),
    })),
    items: (response.data?.items || []).map((item: any) => cartItemMapper(item)),
    addOns: (response.data?.add_ons || []).map((item: any) => mapHotelAddOn(item)),
    ticketProtection: response.data?.ticket_protection
      ? {
        available: !!response.data.ticket_protection.available,
        selected: !!response.data.ticket_protection.selected,
        rate: response.data.ticket_protection.rate,
        eligibleSubtotal: response.data.ticket_protection.eligible_subtotal ?? 0,
        amount: response.data.ticket_protection.amount ?? 0,
        eligibleItemIds: response.data.ticket_protection.eligible_item_ids ?? [],
      }
      : undefined,
  })

const fetchCartData = async ({ id }: { id: string }) => {
  try {
    return mapCartData(await fetchApi(`/cart/${id}`))
  } catch (error) {
    // Another tab may change the shared cookie after a gift card was added.
    // Restore the cart's native currency so it can be read and edited again.
    const required = isFetchApiError(error) && getFetchApiErrorStatus(error) === 422
      ? error.data?.errors?.cart_currency
      : undefined
    const code = normalizeCurrencyCode(Array.isArray(required) ? required[0] : required)
    const cookie = useCurrencyCodeCookie()
    if (!code || cookie.value === code) throw error
    cookie.value = code
    if (import.meta.client) {
      await nextTick()
      window.location.reload()
    }
    // One retry only; a disabled/deleted currency must still surface an error.
    return mapCartData(await fetchApi(`/cart/${id}`, { currency: code }))
  }
}

const mapHotelCity = (city: any) => ({
  id: String(city?.id ?? ''),
  name: city?.name ?? '',
  countryCode: city?.country_code ?? city?.countryCode ?? null,
  latitude: toNumberOrNull(city?.latitude ?? city?.lat),
  longitude: toNumberOrNull(city?.longitude ?? city?.lng),
})

/**
 * The provider city catalogue. Cached server-side, and the store keeps the one
 * response for the session, so the map can resolve a viewport to a city without
 * ever going back to the network.
 */
const cartRequestCurrency = (cartId: string) => {
  const cart = useCartStore().data
  return cart?.id === cartId ? cart.currency ?? undefined : undefined
}

const fetchHotelCities = async ({ cartId }: { cartId: string }) => {
  const response: any = await fetchApi(`/cart/${cartId}/travel-add-ons/hotels/cities`, { currency: cartRequestCurrency(cartId) })

  return hotelCityListSchema.parse({
    provider: response.data?.provider,
    items: (response.data?.items || []).map(mapHotelCity),
  })
}

const fetchHotelDefaults = async ({ cartId }: { cartId: string }) => {
  const response: any = await fetchApi(`/cart/${cartId}/travel-add-ons/hotels/defaults`, { currency: cartRequestCurrency(cartId) })

  return mapHotelSearchCriteria(response.data || {})
}

const searchHotels = async ({
  cartId,
  search,
}: {
  cartId: string
  search: HotelSearchInput
}) => {
  const response: any = await fetchApi(`/cart/${cartId}/travel-add-ons/hotels/search`, {
    method: 'POST',
    currency: cartRequestCurrency(cartId),
    body: { ...search, currency: cartRequestCurrency(cartId) ?? search.currency },
  })

  return mapHotelSearchResult(response)
}

const selectHotel = async ({
  cartId,
  payload,
}: {
  cartId: string
  payload: Record<string, unknown>
}) => {
  const response: any = await fetchApi(`/cart/${cartId}/travel-add-ons/hotels`, {
    method: 'POST',
    currency: cartRequestCurrency(cartId),
    body: payload,
  })

  return mapCartData(response)
}

const removeTravelAddOn = async ({
  cartId,
  addOnId,
}: {
  cartId: string
  addOnId: string
}) => {
  await fetchApi(`/cart/${cartId}/travel-add-ons/${addOnId}`, {
    method: 'DELETE',
    currency: cartRequestCurrency(cartId),
  })
}

const mapPackagePricing = (pricing: any) => ({
  ticketTotal: pricing?.ticket_total ?? 0,
  hotelTotal: pricing?.hotel_total ?? 0,
  total: pricing?.total ?? 0,
  perPerson: pricing?.per_person ?? 0,
  travellers: pricing?.travellers ?? 1,
  nights: pricing?.nights ?? 0,
  currency: pricing?.currency ?? null,
})

/**
 * Same hotels as searchHotels, but every offer carries the price of the whole
 * trip it would complete. Used by the package step, not by the cart add-on flow.
 */
const searchPackageHotels = async ({
  cartId,
  search,
}: {
  cartId: string
  search: HotelSearchInput
}) => {
  const response: any = await fetchApi(`/cart/${cartId}/package/hotels/search`, {
    method: 'POST',
    currency: cartRequestCurrency(cartId),
    body: { ...search, currency: cartRequestCurrency(cartId) ?? search.currency },
  })

  const items = (response.data?.items || []).map((offer: any) => ({
    ...mapHotelOffer(offer),
    package: offer?.package ? mapPackagePricing(offer.package) : null,
  }))

  return hotelSearchResultSchema.parse({
    provider: response.data?.provider,
    criteria: mapHotelSearchCriteria(response.data?.criteria || {}),
    items,
    raw: response.data?.raw,
  }) as HotelSearchData
}

const fetchPackageSummary = async ({ cartId }: { cartId: string }) => {
  const response: any = await fetchApi(`/cart/${cartId}/package`, { currency: cartRequestCurrency(cartId) })
  const data = response.data || {}

  return packageSummarySchema.parse({
    complete: !!data.complete,
    pricing: mapPackagePricing(data.pricing),
    defaults: data.defaults ? mapHotelSearchCriteria(data.defaults) : null,
    tickets: {
      total: data.tickets?.total ?? 0,
      travellers: data.tickets?.travellers ?? 1,
      currency: data.tickets?.currency ?? null,
      items: (data.tickets?.items || []).map((item: any) => ({
        productName: item.product_name ?? null,
        ticketCategory: item.ticket_category ?? null,
        quantity: item.quantity ?? 0,
        dateTime: item.datetime ?? null,
        startDate: item.start_date ?? null,
        startTime: item.start_time ?? null,
        venueName: item.venue_name ?? null,
        venueCity: item.venue_city ?? null,
      })),
    },
    hotel: data.hotel
      ? {
        id: data.hotel.id ?? null,
        name: data.hotel.name ?? null,
        address: data.hotel.address ?? null,
        city: data.hotel.city ?? null,
        image: data.hotel.image ?? null,
        nights: data.hotel.nights ?? 0,
        guestRooms: (data.hotel.guest_rooms || []).map(mapHotelGuestRoom),
        total: data.hotel.total ?? 0,
      }
      : null,
  })
}

export {
  fetchCartData,
  fetchHotelCities,
  fetchHotelDefaults,
  fetchPackageSummary,
  cartItemMapper,
  mapCartData,
  mapHotelAddOn,
  mapHotelGuestRoom,
  removeTravelAddOn,
  searchHotels,
  searchPackageHotels,
  selectHotel,
}

export type PackageSummaryData = Awaited<ReturnType<typeof fetchPackageSummary>>

export type CartData = Awaited<ReturnType<typeof fetchCartData>>
export type HotelCityListData = Awaited<ReturnType<typeof fetchHotelCities>>
export type HotelCity = HotelCityListData['items'][number]
